import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import '../Main.scss';

import {
    Button,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader
} from 'reactstrap';

import { 
    PortalSafeDataCollectionLimitResponse,
    PortalTextDataCollectionLimitDetails,
    PortalTextCommon,
} from '../../Models/Api/strongbox.financialportal';
import {
    actionCreators as UIStateActions,
    GetShowDataCollectionLimitDetails,
} from '../../Store/UIState';
import { ApplicationState } from '../../Store';
import { 
    GetDataCollectionLimits,
} from '../../Store/Tenant';
import {
    GetPortalText,
} from '../../Store/PortalAssets';
import {
    StringFormat,
} from '../../Utils/StringUtils';
import {
    formatStringMDYFullMonth,
    FormatDate,
}from '../../Utils/DateUtils';

type InjectedReduxState = {
    dataCollectionLimits?: PortalSafeDataCollectionLimitResponse;
    showDataCollectionLimitDetails: boolean;
    commonText?: PortalTextCommon;
    collectionLimitsText?: PortalTextDataCollectionLimitDetails;
};

type InjectedActionCreators = typeof UIStateActions;

type DataCollectionLimitDetailsModalProps = {
};

type Props = InjectedReduxState & InjectedActionCreators & DataCollectionLimitDetailsModalProps;

const DataCollectionLimitDetailsModalComponent: React.FC<Props> = (props): React.ReactElement => {
    const {
        commonText,
        collectionLimitsText,
        dataCollectionLimits,
        showDataCollectionLimitDetails,
        SetShowDataCollectionLimitsDetails,
    }  = props;

    const limitText = React.useMemo(() => {
        if (!dataCollectionLimits || !dataCollectionLimits.hasLimit) {
            return '';
        }
        if (!collectionLimitsText) {
            return '';
        }
        return StringFormat(collectionLimitsText.templateDataXOfYDataCollectionsRemaining, dataCollectionLimits.countRemaining, dataCollectionLimits.limit);
    }, [collectionLimitsText, dataCollectionLimits]);

    const descriptionContent: React.ReactElement[] = React.useMemo(() => {
        if ((!collectionLimitsText) || (!dataCollectionLimits) || (!dataCollectionLimits.hasLimit)) {
            return [(<></>)];
        }

        const result: React.ReactElement[] = [];

        if (dataCollectionLimits.countRemaining! <= 0) {
            collectionLimitsText.detailDescriptionsOverLimit.forEach((p, indexParagraph) => {
                result.push((<p key={`collectionLimitDescription-${indexParagraph}`}>{p}</p>))
            });

            if (!!dataCollectionLimits.billingPeriod?.endDate) {
                const dateOfReset = new Date(dataCollectionLimits.billingPeriod.endDate);
                if (!!dateOfReset) {
                    // Will handle roll over of month and such
                    dateOfReset.setDate(dateOfReset.getDate() + 1);
                    const resetDateString = FormatDate(dateOfReset, formatStringMDYFullMonth);
                    if (!!resetDateString) {
                        const resetsOn = StringFormat(collectionLimitsText?.templateDetailDescriptionsOverLimitResetsOn || '{0}', resetDateString);
                        result.push((<p key={`collectionLimitDescription-${collectionLimitsText.detailDescriptionsOverLimit.length}`}>{resetsOn}</p>));
                    }
                }
            }
        } else {
            collectionLimitsText.detailDescriptionsUnderLimit.forEach((p, indexParagraph) => {
                result.push((<p key={`collectionLimitDescription-${indexParagraph}`}>{p}</p>))
            });
        }

        return result;
    }, [collectionLimitsText, dataCollectionLimits])

    const onFinish = (): void => {
        SetShowDataCollectionLimitsDetails(false);
    }

    if (!dataCollectionLimits || !dataCollectionLimits.hasLimit || !showDataCollectionLimitDetails) {
        return (<></>);
    }

    return (
        <Modal
            className={`flight-modal`}
            isOpen={true}
            toggle={onFinish}
            backdrop={'static'}
        >
            <ModalHeader toggle={onFinish}>{collectionLimitsText?.detailsModalHeader || ''}</ModalHeader>
            <ModalBody>
                <div className={`control-region control-region-lender`}>
                    <p>{limitText}</p>
                    <React.Fragment>
                        { descriptionContent }
                    </React.Fragment>
                </div>
            </ModalBody>
            <ModalFooter>
                <Button
                    color="primary"
                    onClick={onFinish}
                >
                    {commonText?.done || ''}
                </Button>
            </ModalFooter>
        </Modal>

    );
}

export const DataCollectionLimitDetailsModal = connect<InjectedReduxState, InjectedActionCreators, DataCollectionLimitDetailsModalProps, ApplicationState>(
    (appState: ApplicationState, props: DataCollectionLimitDetailsModalProps) => {
        const result: InjectedReduxState = {
            dataCollectionLimits: GetDataCollectionLimits(appState),
            showDataCollectionLimitDetails: GetShowDataCollectionLimitDetails(appState),
            commonText: GetPortalText<PortalTextCommon>(appState, 'commonText'),
            collectionLimitsText: GetPortalText<PortalTextDataCollectionLimitDetails>(appState, 'dataCollectionLimitDetails'),
        };

        return result;
    },
    dispatch => bindActionCreators(
        {
            ...UIStateActions,
        },
        dispatch
    )
)(DataCollectionLimitDetailsModalComponent);


